import { useEffect, useState, useCallback } from "react";
import { Container, Nav, Navbar, NavDropdown, Col } from "react-bootstrap";
import { Stack, useColorMode } from "@chakra-ui/react";
import LogoutButton from "./buttons/logout";
import LoginButton from "./buttons/login";
import { Link } from "react-router-dom";
import userAvatarUrl from "./avatar.png";
import Logo from "./TT.svg";
import "../pages/styles/navbar.css";

const TheNavbar = () => {
  const { colorMode } = useColorMode();

  const [authState, setAuthState] = useState({
    isLoggedIn: false,
    userRole: "",
    username: "",
    testCenterId: "",
    tokenExp: null,
  });

  const [isScrolled, setIsScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  // const navigate = useNavigate();

  const parseToken = (token) => {
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        return {
          username: payload.username,
          userRole: payload.role,
          testCenterId: payload.role === "test_center" ? payload.testCenterId : "",
          exp: payload.exp,
        };
      }
    } catch (error) {
      console.error("Invalid token format:", error);
    }
    return null;
  };

  const checkTokenAndRefresh = useCallback(() => {
    const token = localStorage.getItem("token");

    if (token) {
      const parsedToken = parseToken(token);
      if (parsedToken) {
        const isExpired = parsedToken.exp * 1000 < Date.now();

        if (!isExpired) {
          setAuthState({
            isLoggedIn: true,
            userRole: parsedToken.userRole,
            username: parsedToken.username,
            testCenterId: parsedToken.testCenterId,
            tokenExp: parsedToken.exp,
          });
          return;
        } else {
          // Token expired: remove and logout
          localStorage.removeItem("token");
        }
      }
    }

    setAuthState({
      isLoggedIn: false,
      userRole: "",
      username: "",
      testCenterId: "",
      tokenExp: null,
    });
  }, []);

  useEffect(() => {
    checkTokenAndRefresh();
    const intervalId = setInterval(checkTokenAndRefresh, 1000);

    // Check dark mode from localStorage
    const checkDarkMode = () => {
      const darkMode = localStorage.getItem('darkMode') === 'true';
      setIsDarkMode(darkMode);
    };
    
    checkDarkMode();
    window.addEventListener('storage', checkDarkMode);

    // Optimized scroll handler with throttling to prevent forced reflows
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          setIsScrolled(scrollTop > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    // Use passive listener for better performance
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', checkDarkMode);
      window.removeEventListener("scroll", handleScroll, { passive: true });
    };
  }, [checkTokenAndRefresh]);

  // Handle color mode for navbar styling
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", colorMode);
  }, [colorMode]);

  return (
    <Navbar
      expand="lg"
      className={`navbar-custom fixed-top ${isScrolled ? "navbar-scrolled" : ""}`}
    >
      <Container fluid>
        {/* Brand Logo */}
        <Col md={2}>
          <Navbar.Brand as={Link} to="/">
            <img
              src={Logo}
              className="d-inline-block align-top"
              alt="Talent Track"
              width={72}
            />
          </Navbar.Brand>
        </Col>

        {/* Toggle Button */}
        <Navbar.Toggle
          aria-controls="basic-navbar-nav"
          className={colorMode === "dark" ? "navbar-toggle-white" : ""}
        />

        {/* Collapse Content */}
        <Navbar.Collapse id="basic-navbar-nav" className="text-center">
          <Col md={10}>
            <Nav className="me-auto">
              <Nav.Link as={Link} to="/">
                HOME
              </Nav.Link>

              <NavDropdown
                title="HEALTH CARE EXAMS"
                id="health-care-dropdown"
                className="health-care-dropdown"
              >
                {/* <NavDropdown.Item as={Link} to="/Medical-exams/egypt"> Egypt </NavDropdown.Item> */}
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-National-Health-Regulatory-Authority-NHRA"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  Bahrain-NHRA
                </NavDropdown.Item>
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-Kuwait-Ministry-of-Health-MOHKW"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  Kuwait-MOHKW
                </NavDropdown.Item>
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-Department-of-Healthcare-Professions-DHP"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  Qatar-DHP
                </NavDropdown.Item>
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-Saudi-commission-for-Health-Specialties-SCFHS"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  Saudi Arabia-SCFHS
                </NavDropdown.Item>
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-Dubai-Health-Authority-DHA"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  Dubai-DHA
                </NavDropdown.Item>
                <NavDropdown.Item 
                  as={Link} 
                  to="/Prometric-Exams-for-the-Emirates-Ministry-of-Health-Dubai-EMOH"
                  style={{ color: isDarkMode ? '#fff' : '#000', textDecoration: 'none' }}
                  className={isDarkMode ? '' : 'force-dark-text'}
                >
                  UAE-EMOH
                </NavDropdown.Item>



              </NavDropdown>

              {/* HR Exams - Only show when NOT logged in */}
              {!authState.isLoggedIn && (
                <Nav.Link as={Link} to="/hr-exam">
                  HR EXAMS
                </Nav.Link>
              )}

              {/* Book Exam - Only show when NOT logged in */}
              {!authState.isLoggedIn && (
                <Nav.Link as={Link} to="/Contact-us">
                  BOOK EXAM
                </Nav.Link>
              )}
              {/* <Nav.Link as={Link} to="/FAQ">
                FAQ
              </Nav.Link> */}



              {authState.isLoggedIn && authState.userRole !== "reviewer" && (
                <Nav.Link as={Link} to="/Register">
                  REGISTER FOR EXAM
                </Nav.Link>
              )}

              {authState.isLoggedIn &&
              (authState.userRole === "admin" || authState.userRole === "reviewer" || authState.userRole === "auditor" || authState.userRole === "external_user") ? (
                <Nav.Link as={Link} to="/Admin">
                  DASHBOARD
                </Nav.Link>
              ) : authState.isLoggedIn && authState.userRole === "test_center" ? (
                <Nav.Link as={Link} to={`/test-center/${authState.testCenterId}`}>
                  DASHBOARD
                </Nav.Link>
              ) : null}

              {authState.isLoggedIn && authState.userRole !== "external_user" && authState.userRole !== "auditor" && authState.userRole !== "reviewer" && (
                <Nav.Link as={Link} to="/inquiries">
                  INQUIRIES
                </Nav.Link>
              )}

              {/* Sales Dashboard - Only for admin, auditor, reviewer */}
              {authState.isLoggedIn && 
               (authState.userRole === "admin" || authState.userRole === "auditor" || authState.userRole === "reviewer") && (
                <Nav.Link as={Link} to="/sales-dashboard">
                  SALES DASHBOARD
                </Nav.Link>
              )}

              {/* Voucher Management - Only for admin and reviewer */}
              {authState.isLoggedIn && (authState.userRole === "admin" || authState.userRole === "reviewer") && (
                <Nav.Link as={Link} to="/voucher-management">
                  VOUCHERS
                </Nav.Link>
              )}

              <Nav.Link as={Link} to="/about">
                ABOUT US
              </Nav.Link>
            </Nav>
          </Col>

          {/* Right Section (Login / Avatar) */}
          <Col md={2}>
            <Nav>
              {authState.isLoggedIn ? (
                <NavDropdown
                  className="text-center userAvatar"
                  title={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <img
                        src={userAvatarUrl}
                        alt="User Avatar"
                        style={{
                          width: "24px",
                          height: "24px",
                          borderRadius: "50%",
                        }}
                      />
                      <span style={{ marginTop: "6px" }}>
                        {authState.username.toUpperCase()}
                      </span>
                    </Stack>
                  }
                  id="basic-nav-dropdown"
                  style={{ paddingRight: "0" }}
                >
                  <div
                    style={{
                      padding: "0 1rem",
                      textAlign: "center",
                      fontSize: "0.95em",
                    }}
                  >
                    {authState.tokenExp && (
                      <div style={{ color: "#888", marginBottom: 6 }}>
                        Token expires at:
                        <br />
                        <b>
                          {new Date(authState.tokenExp * 1000).toLocaleString()}
                        </b>
                      </div>
                    )}
                    <LogoutButton />
                  </div>
                </NavDropdown>
              ) : (
                <LoginButton />
              )}
            </Nav>
          </Col>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default TheNavbar;
